import { Stack, styled } from '@mui/material';

import { focusRing, forMobile, hoverable, scrollbarStyles, theme } from '@/shared/design-system';
import type { CoverageLevel } from '@/utils/role-comparison';

export const CompareRoot = styled(Stack)({
  flexDirection: 'column',
  gap: theme.spacing[16],
  minWidth: 0,
});

export const CompareToolbar = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing[16],

  [forMobile]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
});

export const ToolbarControls = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing[16],
});

export const RolesPickerSlot = styled('div')({
  width: '200px',
});

export const ToggleLabel = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing[8],
});

/** The matrix is genuinely tabular, so it scrolls inside its own frame rather than reflowing. */
export const MatrixScroller = styled('div')({
  overflow: 'auto',
  maxHeight: '640px',
  border: `1px solid ${theme.colors.border.subtle}`,
  borderRadius: theme.radius[12],
  ...scrollbarStyles,
});

export const Matrix = styled('table')({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,

  '& th, & td': {
    padding: `${theme.spacing[8]}px ${theme.spacing[12]}px`,
    borderBottom: `1px solid ${theme.colors.border.subtle}`,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    backgroundColor: theme.colors.background.canvas,
  },

  '& thead th': {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    verticalAlign: 'bottom',
  },

  // The label column stays put while the role columns scroll sideways.
  '& th:first-of-type': {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    textAlign: 'left',
    minWidth: '240px',
  },

  '& thead th:first-of-type': {
    zIndex: 3,
  },

  '& tr[data-row="group"] th, & tr[data-row="group"] td': {
    backgroundColor: theme.colors.background.default,
  },

  '& tr[data-row="permission"] th': {
    paddingLeft: `${theme.spacing[40]}px`,
  },
});

export const RoleColumnButton = styled('button')({
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing[2],
  padding: `${theme.spacing[4]}px ${theme.spacing[8]}px`,
  border: 'none',
  borderRadius: theme.radius[8],
  background: 'transparent',
  cursor: 'pointer',
  font: 'inherit',
  ...hoverable(theme),
  ...focusRing(theme),
});

export const GroupToggle = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  padding: `${theme.spacing[4]}px`,
  marginLeft: `-${theme.spacing[4]}px`,
  border: 'none',
  borderRadius: theme.radius[8],
  background: 'transparent',
  color: theme.colors.icon.secondary,
  cursor: 'pointer',
  font: 'inherit',
  ...hoverable(theme),
  ...focusRing(theme),

  '& svg': {
    width: '16px',
    height: '16px',
    flexShrink: 0,
  },
});

export const CoverageCell = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing[4],
});

const COVERAGE_FILL: Record<CoverageLevel, string> = {
  full: theme.colors.brand.solid,
  partial: `linear-gradient(90deg, ${theme.colors.brand.solid} 50%, ${theme.colors.border.default} 50%)`,
  none: 'transparent',
};

export const CoverageDot = styled('span', {
  shouldForwardProp: (prop) => prop !== '$level',
})<{ $level: CoverageLevel }>(({ $level }) => ({
  display: 'inline-block',
  width: '10px',
  height: '10px',
  flexShrink: 0,
  borderRadius: theme.radius.full,
  background: COVERAGE_FILL[$level],
  border: $level === 'none' ? `1px solid ${theme.colors.border.strong}` : 'none',
}));

export const Legend = styled(Stack)({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing[16],
});

export const LegendItem = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing[6],
});
